import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { distinctUntilChanged, first, firstValueFrom, Observable, Subject, Subscription } from 'rxjs';
import { GlobalState, GlobalQuery, GlobalService } from '../../_core/state/global';
import { fade } from '../../_core/utils/animations.utils';
import { convertProjectStateToCartItems, Project, ProjectsQuery, ProjectsService, ProjectStatus } from '../../_core/state/project';
import { ActivatedRoute, Router } from '@angular/router';
import { take, takeUntil, tap } from 'rxjs/operators';
import { CartItem, CartItemGroup, CartItemType, PublicProjectCartItem } from '../../_core/state/cart/cart.model';
import { CartService } from '../../_core/state/cart/cart.service';
import { CartQuery } from '../../_core/state/cart/cart.query';
import { ConfirmDialogComponent } from '../../shared/components/confirm-dialog/confirm-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { DEFAULT_CART_ITEM_NAME } from '../../../../../api/src/cart/project-cart-item.entity';
import { CreativeUnitPackageDto } from '../../../../../api/src/creative-unit-package/models/creative-unit-package.dto';
import { Profile, SessionQuery } from '../../_core/state/session';
import { TaskStatus } from '../../../../../api/src/encode/encode-task.entity';
import { AnalyticsService } from '../../_core/services/analytics.service';
import { ProjectSyncingProviderDialogComponent } from './project-syncing-provider-dialog/project-syncing-provider-dialog.component';
import { AdPackageSelectDialogService } from '../../_core/state/ad/ad-package/ad-package-select-dialog.service';
import { PublicSyncingProvider } from '../../../../../api/src/syncing-provider/syncingProvider.entity';
import { PublicDataSource } from '../../../../../api/src/data-source/data-source.entity';
import { DataSourceService } from '../../_core/state/data-source/data-source.service';
import { DataSourceConnectorType } from '../../../../../api/src/data-source/models';
import { JobStatus, PublicEncodeJob } from '../../../../../api/src/encode/encode-job.entity';
import { CreativeUnitPackageService } from '../../_core/state/ad/ad-unit-package.service';
import { EncodeJobQuery } from '../../_core/state/encode-job/encode-job.query';
import { NavState } from '../../shared/components/header/header.model';
import { PublicCreativeUnitPackageListing } from '../../../../../api/src/creative-unit-package-listing/creative-unit-package-listing.entity';
import { CreativeUnitPackageListingService } from '../../_core/state/creative-unit-package-listing/creative-unit-package-listing.service';
import { GlobalSettings } from '../../_core/state/global/global.model';
import { ProjectActionsService } from '../projects/services/project-actions.service';
import { SessionService } from '../../_core/state/session/session.service';
import { ProjectPremiumContentStatus, Status } from '../../../../../api/src/project/project.entity'
import { MatSelectChange } from '@angular/material/select'
import { AdStudioContainerService } from '../../_core/state/ad/ad-container.service';
import { BusinessUtils } from '../../_core/state/business/business.utils';
import { CreativePackageUtils } from '../../../../../api/src/creative-unit-package/utils/creative-package.utils';
import { ObjectUtils } from '../../../../../api/src/_core/utils';
import { CartUtils } from '../../_core/state/cart/cart.utils';
import { EncodeJobService } from "../../_core/state/encode-job/encode-job.service";

export enum ProjectState {
	Project = 'project',
	ProjectCheckout = 'project-checkout',
	AdStudio = 'ad-studio',
	VideoBuilder = 'video-builder',
	Download = 'download',
	Settings = 'settings',
	Export = 'export',
	AddTemplate = 'add-template'
}

export enum DeleteCartItemMode {
	Package = 'package',
	Group = 'group',
	Item = 'item'
}

export interface SelectedPartialListingEvent {
	item?: CartItem;
	group: CartItemGroup;
	listing: PublicCreativeUnitPackageListing;
}

@Component({
	selector: 'app-project',
	animations: [fade('fade', 200)],
	templateUrl: 'project.page.html',
	styleUrls: ['project.page.scss']
})
export class ProjectPage implements OnInit, OnDestroy {
	readonly convertProjectStateToCartItems = convertProjectStateToCartItems;
	public readonly checkoutDisabledTooltip =
		"All of your project's creative packages need to be ready for purchase to proceed to checkout.";
	public readonly DataSourceConnectorType = DataSourceConnectorType;
	public readonly JobStatus = JobStatus;
	public readonly reviewEnabled = true;
	readonly NEW_ITEM_ID_PREFIX = 'temp-';
	public dataSourceOptions: PublicDataSource[] = [];

	public state$: Observable<GlobalState['state']>;

	public loading$: Observable<boolean>;
	public adminMode$: Observable<boolean>;
	public projectCartItems$: Observable<PublicProjectCartItem[]>;
	public activeEncodeJob$: Observable<PublicEncodeJob>;

	public project$: Observable<Project>;
	public activeProject$: Observable<Project>;
	public state: ProjectState;
	public exportHistory$: Observable<PublicEncodeJob[]>;
	public receiptLoading = false;
	public projectReviewState = false

	public projectConfig: { mode: 'list' | 'checkout' | 'post-checkout' | 'settings' } = {
		mode: 'list'
	};

	public adStudioConfig: {
		package?: CreativeUnitPackageDto;
		listing?: PublicCreativeUnitPackageListing;
		group?: CartItemGroup;
		item?: CartItem;
		itemIds?: string[];
	} = {
		package: undefined,
		listing: undefined,
		group: undefined,
		item: undefined,
		itemIds: undefined
	};

	public videoBuilderConfig: { cartItem: CartItem } = {
		cartItem: undefined
	};
	public canPurchase = false;

	public exportActive = false;
	public disableCheckout = false;
	public projectStateExportEnabled = false;
	public exportsRequireApproval = false;
	public downloadReceiptEnabled = false;
	public disableReceiptButton = false;

	public readonly projectState = ProjectState;
	public readonly navStateEnum = NavState;
	protected readonly ProjectStatus = Status

	public actionInProgress = null;

	public projectId: string;

	private syncProvidersSubscriptions: Subscription[] = [];
	private syncProvidersData: any[] = [];

	private _unsubscribe: Subject<void> = new Subject<void>();
	public siteCopy$: Observable<Partial<GlobalSettings>>;

	public creativePackageRequestFormId: string;

	public enableNewCreativePackageRequests: boolean;
	public user: Profile;
	public proj: Project;
	public siteSettings$: Observable<GlobalSettings>;
	public isSuperAdmin: boolean;
	public isProjectFinished = false;
	public adStudioProjectLoaded = true;

	public isGuestUser = false;

	private siteSettings = this.globalQuery.getValue().settings;

	constructor(
		private readonly globalQuery: GlobalQuery,
		private readonly globalService: GlobalService,
		private readonly projectsQuery: ProjectsQuery,
		private readonly dataSourceService: DataSourceService,
		private readonly projectsService: ProjectsService,
		private readonly cartService: CartService,
		private readonly projectCartQuery: CartQuery,
		private readonly router: Router,
		private readonly route: ActivatedRoute,
		private readonly dialog: MatDialog,
		private readonly sessionQuery: SessionQuery,
		private readonly creativeUnitPackageService: CreativeUnitPackageService,
		private readonly creativeUnitPackageListingService: CreativeUnitPackageListingService,
		private readonly encodeJobQuery: EncodeJobQuery,
		private readonly cd: ChangeDetectorRef,
		private readonly projectActionsService: ProjectActionsService,
		private readonly analytics: AnalyticsService,
		private readonly adPackageSelectDialogService: AdPackageSelectDialogService,
		private readonly sessionService: SessionService,
		private readonly adStudioContainerService: AdStudioContainerService,
		private readonly encodeJobService: EncodeJobService
	) {
		this.projectActionsService.projectCreating.next(false);
		this.siteCopy$ = this.globalQuery.copy$;

		if (!this.route.snapshot.queryParams?.keepItems) {
			this.cartService.clear();
		}

		const isAdStudioState = !!this.route.snapshot.queryParams?.packageId;
		if (!isAdStudioState) {
			this.setState(ProjectState.Project);
		}
		this.activeProject$ = this.projectsQuery.selectActive().pipe(
			takeUntil(this._unsubscribe),
			distinctUntilChanged(),
			tap(project => {
				this.globalService.setTitle(project?.name ?? ($localize `:New Project Title Text@@newProjectTitleText:New Project`));
				if (project?.status === ProjectStatus.Finished) {
					this.globalService.setState('download');
					this.goToDownload();
				}
				if (project.version === '2' && (!project.cartItems || project.cartItems.length === 0)) {
					if (!isAdStudioState && this.state !== ProjectState.Settings && this.state != ProjectState.VideoBuilder && this.state !== ProjectState.Export) {
						this.goToAddTemplate();
					}
				}

				this.isGuestUser = this.sessionQuery.isGuestOfBusiness(project?.business?.id) && !this.sessionQuery.isAtLeastAdmin();
				// this.startCheckingForSmartsheetChanges(
				// 	project.syncingProviders?.filter(sp => sp.dataSource.connectorType == "smartsheet")
				// );
			})
		);

		this.loading$ = this.projectsQuery.selectLoading();
		this.adminMode$ = this.globalQuery.select('adminMode');

		this.projectCartItems$ = this.projectCartQuery.selectActiveItemsFromCart$;

		this.siteSettings$ = this.globalQuery.select('settings');
		this.isSuperAdmin = this.sessionQuery.isAtLeastSuperAdmin();
		this.adStudioContainerService.setPreviewContext('preview');
	}

	ngOnInit() {
		this.projectsService.setLoading(true);
		this.projectId = this.route.snapshot?.params?.id;

		// Detect export via route path, route data, or query param
		const isExportDirect = this.route.routeConfig?.path === 'export' || this.route.snapshot.data?.projectState === 'export';
		const queryState = this.route.snapshot?.queryParams?.state;
		const isExportQuery = queryState === 'export';
		console.log('[ProjectPage] ngOnInit detection', {
			parentPath: this.route.routeConfig?.path,
			data: this.route.snapshot.data,
			urlSegs: (this.route.snapshot.url || []).map(s => s.path),
			isExportDirect, isExportQuery, queryState
		});
		if (isExportDirect || isExportQuery) {
			console.log('[ProjectPage] Setting Export state early');
			this.setState(ProjectState.Export);
			this.globalService.setState('export');
		}
		// Late child check in case of timing issues
		setTimeout(() => {
			const child = this.route.firstChild;
			const childExport = child?.routeConfig?.path === 'export' || child?.snapshot?.data?.projectState === 'export';
			if (childExport && this.state !== ProjectState.Export) {
				console.log('[ProjectPage] Late child export detection, updating state');
				this.setState(ProjectState.Export);
				this.globalService.setState('export');
			}
		}, 0);

		this.globalQuery.authenticatedSettings$.pipe(takeUntil(this._unsubscribe)).subscribe(settings => {
			this.exportsRequireApproval = settings?.exportsRequireApproval;
			this.projectStateExportEnabled = settings?.projectStateExportEnabled;
			this.disableCheckout = settings?.disableCheckout;
			this.downloadReceiptEnabled = settings?.downloadReceiptEnabled;
			this.disableReceiptButton = settings?.disableReceiptButton;
			this.creativePackageRequestFormId = settings?.creativePackageRequestFormId;
			this.enableNewCreativePackageRequests = settings?.enableNewCreativePackageRequests;
			this.projectReviewState =  settings?.enableProjectComments && this.route.snapshot.queryParams?.reviewState === 'true';

			if (this.projectId) {
				// Set the app tabs to blank while we load up the project.
				this.globalService.setPages([]);
				const dehydratedData =
					!(
						!!this.route.snapshot.queryParams?.activeItemId ||
						!!this.route.snapshot.queryParams?.packageId ||
						!!this.route.snapshot.queryParams?.listingId ||
						!!this.route.snapshot.queryParams?.itemIds
					) || this.state === ProjectState.VideoBuilder;
				this.project$ = this.projectsService.getOne(this.projectId, false, false, dehydratedData).pipe(
					 tap(project => {
						 this.isGuestUser = this.sessionQuery.isGuestOfBusiness(project?.business?.id) && !this.sessionQuery.isAtLeastAdmin();

						 this.proj = project;
						 this.globalService.setTitle(project?.name ?? ($localize `:New Project Title Text@@newProjectTitleText:New Project`));

						 if (project.version === '1') {
							 // clear active cart item
							 this.cartService.clearActive();
							 this.goToProjectVideoBuilder();
							}

						 if (project?.status === ProjectStatus.Finished) {
							 this.isProjectFinished = true;
							 this.goToDownload();
							}

						 const groupId = this.route.snapshot.queryParams?.groupId?.trim();
						 const itemIds = this.route.snapshot.queryParams?.itemIds?.split(',').map((id: string) => id.trim()) || [];
						 const activeItemId = this.route.snapshot.queryParams?.activeItemId?.trim();

						 // do not push the active item into the itemIds array, because this dictates visibility and we want all items to be visible
						 // if (activeItemId) {
							// 	itemIds.push(activeItemId);
							// }

							let item: CartItem;
							let group: CartItemGroup;

							if (activeItemId) {
								item = this.projectCartQuery.getAll()?.find(item => item?.id === activeItemId);
							} else if (groupId) {
								const groupItems = this.projectCartQuery.getItemsFromSameItemDataGroup(groupId);

								group = {
									...groupItems?.[0],
									id: groupId,
									items: groupItems || []
								} as any;
							}

							// Get the parameters from the url.
							this.adStudioConfig = {
								package: null,
								group,
								item,
								itemIds
							};

							this.projectsService.setActive(project.id);

							this.encodeJobQuery
								.selectLatestExport(project.id as string)
								.pipe(takeUntil(this._unsubscribe))
								.subscribe((encodeJob: PublicEncodeJob) => {
									this.exportActive = encodeJob?.status === JobStatus.Pending || encodeJob?.status === JobStatus.Active;
								});

							this.exportHistory$ = this.encodeJobQuery.selectExportHistory(project.id as string);

							this.canPurchase =
								this.sessionQuery.isAtLeastAdmin() || this.sessionQuery.canPurchaseForBusiness(project?.business.id);

							// See if the route has 'ad-studio' in it.
							if (this.route.snapshot?.url?.length > 0) {
								const url = this.route.snapshot.url[0].path;
								if (url === 'ad-studio') {
									this.goToAdStudioState();
								} else if (url === 'add') {
									this.setState(ProjectState.AddTemplate);
								}
							}

							// this.state = ProjectState.AddTemplate;

							// this.startCheckingForSmartsheetChanges(
							// 	project.syncingProviders?.filter(sp => sp.dataSource.connectorType == 'smartsheet')
							// );

							this.sessionService.getUserStatus(this.sessionQuery.getToken()).subscribe(response => {
								this.user = response.profile;
							});
						},
						err => {
							console.log('Error loading project', err);
							this.presentError(err.error.statusCode);
							this.router.navigate(['']);
						}
					)
				);
			} else {
				this.router.navigate(['']);
			}
		});
		this.loadDataSourceOptions();
	}

	loadDataSourceOptions() {
		this.dataSourceService.find({ sortBy: {id: 'created', direction: 'DESC'}}).subscribe(response => {
			this.dataSourceOptions = response.data?.results || [];
		});
	}

	pluckPackageFromCartItems(items: CartItem[], id: CreativeUnitPackageDto['id']): CreativeUnitPackageDto {
		return items?.find(item => item?.itemData?.packageId === id)?.itemData?.package;
	}

	ngOnDestroy() {
		this._unsubscribe.next();
		this._unsubscribe.complete();
	}

	goToItem(item: CartItem) {
		if (item.type === CartItemType.AD_UNIT) {
			this.goToAdStudio(undefined, item);
		}
		if (item.type === CartItemType.VIDEO) {
			this.goToVideoBuilderState(item);
		}
	}

	goToAdPackage(pkg: CreativeUnitPackageDto) {
		this.adStudioConfig.package = pkg;

		this.router.navigate(['/project', this.projectsQuery.getActiveId(), 'ad-studio'], {
			queryParams: {
				packageId: pkg?.id
			}
		});
		this.cd.detectChanges();
	}

	goToListing(listing: PublicCreativeUnitPackageListing) {
		this.adStudioConfig.listing = listing;

		if (listing?.isLegacyVideoTemplate) {
			this.createVideoCartItem(listing);
			return;
		}

		this.router.navigate(['/project', this.projectsQuery.getActiveId(), 'ad-studio'], {
			queryParams: {
				listingId: listing?.id
			}
		});
		this.cd.detectChanges();
	}

	async addListingGroupToCart(group: CartItemGroup, listing: PublicCreativeUnitPackageListing, item?: CartItem) {
		const locale = this.globalService.getLocale();
		const activeBusiness = this.projectsQuery.getActive()?.business;
		const projectId = this.projectsQuery.getActiveId();

		const firstItem = CartUtils.getFirstItemOfGroup(group);
		const groupId = firstItem.metadata?.group?.id || firstItem.metadata?.group?.instanceId;
		const packageId = firstItem.metadata?.packageId;

		// Check if the group already exists in the cart
		const existingItems = this.projectCartQuery.getItemsFromPackage(packageId);

		if (existingItems?.length) {
			const duplicates = existingItems?.filter(i => {
				if (groupId) {
					return i.metadata?.group?.id === groupId || i.metadata?.group?.instanceId === groupId;
				} else {
					return item.name === i.metadata?.templateCreativeUnit;
				}
			});

			if (duplicates?.length) {
				console.log('Listing item or group already present in cart, skipping addListingGroupToCart');

				this.goToAdStudio(
					group,
					item ? duplicates.find(i => item.id === i.itemData?.creativeUnitId) : null
				);

				return;
			}
		}

		const activePackage = await firstValueFrom(this.creativeUnitPackageService.findOne(packageId));
		if (!activePackage) {
			console.warn('Unable to find package');
			return;
		}

		this.adStudioConfig.package = activePackage;
		this.adStudioConfig.listing = listing;

		const pkgListing = CreativePackageUtils
			.getListingsFromPackage(activePackage, true)
			.filter(u => !u.hidden);

		let unitListing = pkgListing.find(l => l.id === groupId || l.id === item?.id);
		if (!unitListing || !unitListing?.creativeUnitIds?.length) {
			console.warn('Unable to find unit listing');
			return;
		}

		const units = [];

		for (const id of unitListing.creativeUnitIds) {
			units.push(
				await this.creativeUnitPackageService.createCreativeUnitFromListing(
					unitListing,
					activePackage,
					id,
					{ business: activeBusiness },
					false,
				)
			);
		}

		const promises = [];

		for (const [index, unit] of units.entries()) {
			let name = unit.locale?.['name']?.[locale] || unit.name;

			let newItem = {
				id: this.NEW_ITEM_ID_PREFIX + new Date().getTime().toString() + Math.random().toString(),
				projectId,
				type: CartItemType.AD_UNIT,
				name,
				itemData: CreativePackageUtils.createCreativeUnitWithCustomizations(activePackage, {
					...unit,
					templateCreativeUnit: unit.templateCreativeUnit || unit.name,
					packageId: activePackage.id,
					position: index
				}),
				locale: {
					templateCreateUnit: unit.locale
				}
			};

			// if (newItem.itemData?.customizations) {
			// 	newItem.itemData.customizations = await this.adStudioContainerService.applyDynamicDefaultValues(
			// 		newItem.itemData.customizations,
			// 		undefined,
			// 		activeBusiness,
			// 		newItem.itemData
			// 	);
			// }

			// Add the item to cart
			promises.push(
				this.cartService.add(projectId.toString(), newItem, true).toPromise()
			);
		}

		await Promise.all(promises)
			.then((items) => {
				this.globalService.triggerSuccessMessage(
					$localize `:Added Message@@successMessageAddedGroupToCart:${ObjectUtils.getLocale(unitListing.name, unitListing.locale?.name)} Added`,
					{ horizontalPosition: 'left' }
				);

				this.goToAdStudio(
					group,
					item ? items.find(i => item.id === i.itemData?.creativeUnitId) : null,
					true
				);
			})
			.catch(e => {
				console.error('Error adding partial group to cart', e);
				this.globalService.triggerCustomMessage(e);
			})
	}

	goToGroup(group: CartItemGroup) {
		if (group.type === CartItemType.AD_UNIT) {
			this.goToAdStudio(group);
		} else if (group.type === CartItemType.VIDEO) {
			this.goToVideoBuilderState(group.items[0]);
		}
	}

	goToAdStudio(group?: CartItemGroup, item?: CartItem, keepItems: boolean = false) {
		const firstItem = CartUtils.getFirstItemOfGroup(group);

		let listingId;
		if (group && firstItem) {
			listingId = firstItem.metadata?.listingId;
		} else {
			listingId = item?.metadata?.listingId;
		}

		let packageId;
		if (!listingId) {
			packageId = group?.parent?.id || item?.itemData?.packageId || item?.metadata?.packageId || firstItem.metadata?.packageId;
		}

		let groupId;
		if (item) {
			groupId = item?.metadata?.group?.instanceId || item?.metadata?.group?.id;
		} else if (group?.parentType === 'group') {
			groupId = firstItem?.metadata?.group?.instanceId || firstItem?.metadata?.group?.id;
		}

		// Update the url without actually navigating anywhere.
		this.router.navigate(['ad-studio'], {
			relativeTo: this.route,
			queryParams: {
				packageId,
				listingId,
				groupId,
				activeItemId: item?.id,
				keepItems: keepItems ? true : null,
				reviewState: true
			}
		});
	}

	goToProjectVideoBuilder(item?: CartItem) {
		this.setState(ProjectState.VideoBuilder);
	}

	async deleteCartItems(projectId: Project['id'], items: CartItem[] | PublicProjectCartItem[], mode: DeleteCartItemMode) {
		let entityName: string, message: string;

		switch (mode) {
			case DeleteCartItemMode.Package:
				entityName = $localize`:Delete CartItem Entity Name@@deleteCartItemPackageEntityName:Package`
				message = $localize`:Delete CartItem Message@@deleteCartItemPackageMessage:This will remove all cart items of this package.`;
				break;
			case DeleteCartItemMode.Group:
				entityName = $localize`:Delete CartItem Entity Name@@deleteCartItemGroupEntityName:Cart Item Group`
				message = $localize`:Delete CartItem Message@@deleteCartItemGroupMessage:This will remove all cart items of this group.`;
				break;
			case DeleteCartItemMode.Item:
				entityName = $localize`:Delete CartItem Entity Name@@deleteCartItemItemEntityName:Cart Item`
				message = $localize`:Delete CartItem Message@@deleteCartItemItemMessage:This will remove this item from your cart.`;
				break;
			default:
				console.error('Invalid mode for deleteCartItems');
				return;
		}

		const dialogRef = this.dialog.open(ConfirmDialogComponent, {
			data: {
				entityName,
				message,
				remove: true
			},
			disableClose: false
		});

		dialogRef
			.afterClosed()
			.pipe(first())
			.subscribe(async result => {
				if (result) {
					for (const item of items) {
						await this.cartService.remove(projectId, item.id).toPromise();
					}
					this.refreshCart();
				}
			});
	}

	async createVideoCartItem(listing?: PublicCreativeUnitPackageListing) {
		let item = {
			id: this.NEW_ITEM_ID_PREFIX + new Date().getTime().toString() + Math.random().toString(),
			name: DEFAULT_CART_ITEM_NAME,
			projectId: this.projectsQuery.getActive()?.id,
			type: CartItemType.VIDEO,
			itemData: undefined,
			metadata: {
				thumbnail: listing.thumbnail
			}
		};

		const newItem = await this.cartService.add(item.projectId, item, true).toPromise();

		this.goToVideoBuilderState(newItem);
	}

	async showExternalLinkDialog(syncingProvider?: PublicSyncingProvider) {
		await this.dialog
			.open(ProjectSyncingProviderDialogComponent, {
				data: {
					projectId: this.projectsQuery.getActiveId() as string,
					syncingProvider,
					dataSources: this.dataSourceOptions
				},
				disableClose: true,
				panelClass: ['fullscreen', 'max-width-md', 'background-color']
			})
			.afterClosed()
			.toPromise();
	}

	async deleteExternalLink(syncingProvider: PublicSyncingProvider) {
		const dialogRef = this.dialog.open(ConfirmDialogComponent, {
			data: {
				entityName: $localize`:Delete Confirm Dialog@@externalLinkDialogRemoveConfirmDialogEntityName:External creative package link`,
				message: $localize`:Delete Confirm Dialog@@externalLinkDialogRemoveConfirmDialogMessage:This will remove all cart items from this external link.`,
				remove: true
			},
			disableClose: false
		});

		dialogRef
			.afterClosed()
			.pipe(first())
			.subscribe(async result => {
				if (result) {
					await this.projectsService
						.deleteSyncingProvider(this.projectsQuery.getActiveId() as string, syncingProvider.id)
						.toPromise();
				}
			});
	}

	private needsListingLoad() {
		if (this.route.snapshot.queryParams.listingId) {
			return !this.adStudioConfig.listing || (this.adStudioConfig.listing?.id !== this.route.snapshot.queryParams.listingId)
		}
		return false;
	}

	private needsPackageLoad() {
		if (this.route.snapshot.queryParams.packageId) {
			return !this.adStudioConfig.package || (this.adStudioConfig.package?.id !== this.route.snapshot.queryParams.packageId);
		}
		return false;
	}

	private setAdStudioState(prepareCartItem: boolean = false) {
		this.state = ProjectState.AdStudio;

		if (this.adStudioConfig.item && prepareCartItem) {
			this.adStudioConfig.item = this.cartService.prepareCartItemWithPackage(this.adStudioConfig.item);
		}

		this.adStudioProjectLoaded = true;
	}

	goToAdStudioState() {
		this.adStudioProjectLoaded = false;

		if (this.needsListingLoad()) {
			const { listingId, packageId } = this.route.snapshot.queryParams;

			this.creativeUnitPackageListingService.getOne(listingId, packageId).subscribe(response => {
				this.adStudioConfig.listing = response.data as PublicCreativeUnitPackageListing;
				this.adStudioConfig.package = response.data?.creativeUnitPackage as CreativeUnitPackageDto;

				if (this.adStudioConfig.package) {
					this.creativeUnitPackageService.upsert(this.adStudioConfig.package);
					this.setAdStudioState(true);
				} else {
					this.setAdStudioState(false);
				}
			});
		} else if (this.needsPackageLoad()) {
			this.creativeUnitPackageService.findOne(this.route.snapshot.queryParams.packageId).subscribe(pkg => {
				this.adStudioConfig.package = pkg;
				this.setAdStudioState(true);
			});
		} else {
			this.setAdStudioState(false);
		}
	}

	goToAddTemplate() {
		this.setState(ProjectState.AddTemplate);
		this.cd.detectChanges();
	}

	goToVideoBuilderState(item: CartItem) {
		this.projectsService.setLoading(true);
		if (item?.id?.startsWith(this.NEW_ITEM_ID_PREFIX)) {
			// new cart item
			this.videoBuilderConfig.cartItem = item;
			this.setState(ProjectState.VideoBuilder);
		} else {
			this.cartService
				.getOne(this.projectId, item.id)
				.pipe(first())
				.subscribe(cartItem => {
					this.videoBuilderConfig.cartItem = cartItem;
					this.setState(ProjectState.VideoBuilder);
				});
		}
	}

	goToProjectState(redirect = true, previousState?: string) {
		this.router.navigate(['project', this.projectsQuery.getActive()?.id]);
		this.setState(ProjectState.Project);
		if (previousState === ProjectState.Export) {
			return;
		}
		this.refreshCart(redirect);
	}

	goToCheckout() {
		if (this.proj?.business.freeTrialActive && this.proj?.business.hasActiveSubscription === false && this.siteSettings.freeContentTaggingEnabled === true){
			firstValueFrom(this.projectsService.hasPremiumContent(this.projectId)).then((premiumContent: ProjectPremiumContentStatus) => {
				if (premiumContent?.status === true) {
					this.showTrialPurchaseDialog(premiumContent);
					return;
				}

				this.setState(ProjectState.ProjectCheckout);
				this.projectConfig.mode = 'checkout';
			}).catch((error) => {
				console.error('Error checking premium content');
			})
		}else {
			this.setState(ProjectState.ProjectCheckout);
			this.projectConfig.mode = 'checkout';
		}
	}

	public showTrialPurchaseDialog(premiumContent: ProjectPremiumContentStatus) {
		const freeTrialPurchaseDialogCopy = BusinessUtils.generateFreeTrialPurchaseDialogCopy(premiumContent, this.siteSettings.freeTrialPurchaseDialogCopy, this.projectCartQuery.getAll());

		let dialogRef = this.dialog.open(ConfirmDialogComponent, {
			data: {
				...freeTrialPurchaseDialogCopy,
				hideConfirm: !this.sessionQuery.isOwnerOfBusiness(this.projectsQuery.getActive()?.businessId)
			}
		});

		dialogRef.afterClosed().pipe(first()).subscribe((result: boolean) => {
			if (result) {
				this.router.navigate(['/setup-subscription/' + this.projectsQuery.getActive()?.businessId], {
					replaceUrl: true,
					state: {
						previousState: window.location.pathname
					}
				});
			}
		})


	}

	goToDownload() {
		this.setState(ProjectState.Download);
		this.projectConfig.mode = 'post-checkout';
		this.globalService.setState('download');
	}

	goToExport() {
		const id = this.projectsQuery.getActiveId();
		if (id) {
			this.router.navigate(['/project', id, 'export'], { replaceUrl: true });
			this.setState(ProjectState.Export);
			this.globalService.setState('export');
		}
	}

	goToHome() {
		this.refreshCart();
		this.router.navigate(['']);
	}

	goToProjectSettings() {
		this.setState(ProjectState.Settings);
		this.projectConfig.mode = 'settings';
	}

	goBack(state: string) {
		if (['add-template', 'project-checkout', 'settings'].includes(state)) {
			this.goToProjectState();
		} else if (state === 'export') {
			if (this.projectsQuery.getActive().status === ProjectStatus.Finished) {
				this.goToDownload();
			} else {
				// navigate back to base project route removing /export segment
				const id = this.projectsQuery.getActiveId();
				this.router.navigate(['/project', id], { replaceUrl: true });
				this.goToProjectState(undefined, state);
			}
		}
	}

	public async presentError(code: string) {
		this.globalService.triggerErrorMessage(
			undefined,
			$localize`:Error Message@@projectAccessError:The project doesn't exist or you don't have access.`
		);
	}

	refreshCart(redirect = true) {
		this.cartService.set([]);
		this.projectsService
			.getOne(this.projectsQuery.getActiveId() as string, false, false, true)
			.pipe(
				take(1),
				tap(project => {
					this.cartService.setCartItemsFromProjectId(project.id);
					if (redirect && project.version === '2' && (!project.cartItems || project.cartItems.length === 0)) {
						this.goToAddTemplate();
					} else {
						this.setState(ProjectState.Project);
						this.projectConfig.mode = 'list';
					}
				})
			)
			.subscribe();
	}

	canCheckout(project: Project, projectCartItems: CartItem[], ref: ProjectPage): boolean {
		if (project.version === '1') {
			const latestEncodeTask = ref?.projectsQuery.getLatestEncodeJob(project.encodeJobs)?.encodeTasks?.[0];
			return latestEncodeTask?.status === TaskStatus.Completed && !!latestEncodeTask.result?.path;
		}

		if (!projectCartItems?.length) {
			return false;
		}

		if (ref.reviewEnabled && projectCartItems.some(item => item.approved !== true)) {
			return false;
		}

		// check also for other statues if needed

		return true;
	}

	onCustomEvent(ev: { name: string; args: any[] }) {
		console.log('Custom Event', ev);
		switch (ev.name) {
			case 'requestNewCreativePackage':
				this.onRequestNewCreativePackage(ev.args);
				break;
			default:
				break;
		}
	}

	async onRequestNewCreativePackage(args: any[]) {
		console.log('Request New Creative Package', args);

		if (this.user && this.proj) {
			// window['FreshworksWidget'](
			// 	'identify',
			// 	'ticketForm',
			// 	{
			// 		name: this.user?.fullName,
			// 		email: this.user?.email
			// 	},
			// 	{
			// 		formId: this.creativePackageRequestFormId
			// 	}
			// );

			await new Promise(f => {
				setTimeout(f, 1000);

				window['FreshworksWidget'](
					'identify',
					'ticketForm',
					{
						name: this.user?.fullName,
						email: this.user?.email,
						custom_fields: {
							cf_business_name: this.proj?.business?.name
						}
					},
					{
						formId: this.creativePackageRequestFormId
					}
				);

				setTimeout(f, 1000);
				window['FreshworksWidget']('open', 'ticketForm', {
					formId: this.creativePackageRequestFormId
				});
				console.log('this.proj?', this.proj);
			});
		}
	}

	async downloadReceipt() {
		// this.analytics.setDimension('businessId', this.projectsQuery.getActive().businessId);
		// this.analytics.sendEvent('project', 'download_video_receipt');
		this.receiptLoading = true;
		const data = await this.projectsService.downloadReceipt(this.projectsQuery.getActiveId() as string).toPromise();
		const fileName = ('Receipt_' + this.projectsQuery.getActiveId() as string) + '_' + Math.floor(Date.now() / 1000) + '.pdf';
		this.encodeJobService.downloadExportFromBlob(data, fileName);
		this.receiptLoading = false;
	}

	async cartItemGroupAction({ action, group }) {
		switch (action) {
			case 'approve':
			case 'reject':
				const items = CartUtils.extractGroupItems(group);
				const projectCartItemReviewDtos = items.map(item => ({ cartItemId: item.id, approved: action === 'approve' }));
				this.actionInProgress = { group: group, itemIds: items.map(ci => ci.id) };
				await this.cartService
					.addReview(this.projectsQuery.getActiveId() as string, { items: projectCartItemReviewDtos })
					.toPromise();
				this.actionInProgress = null;
				break;
		}
	}
	async cartItemAction({ action, item }) {
		switch (action) {
			case 'approve':
			case 'reject':
				this.actionInProgress = { itemIds: [item.id] };
				await this.cartService
					.addReview(this.projectsQuery.getActiveId() as string, {
						items: [{ cartItemId: item.id, approved: action === 'approve' }]
					})
					.toPromise();
				this.actionInProgress = null;
				break;
		}
	}

	async changeProjectStatus(event: MatSelectChange) {
		await this.projectsService.update(
			this.projectsQuery.getActiveId() as string,
			{
				status: event.value
			},
			true
		);

		window.location.reload();
	}

	onCartJobSubmit(encodeJob: PublicEncodeJob) {
		this.projectsService.setActiveEncodeJob(encodeJob);
		this.projectsService.addEncodeJobToProject(encodeJob, this.projectsQuery.getActive());
	}

	// private startCheckingForSmartsheetChanges(providers: PublicSyncingProvider[]) {
	// 	const providerInterval: number = 5000;
	// 	for (let subscription of this.syncProvidersSubscriptions) {
	// 		subscription.unsubscribe();
	// 	}
	// 	this.syncProvidersSubscriptions = [];
	// 	this.syncProvidersData = [];
	// 	if (!providers) return;
	// 	for (let i = 0; i < providers.length; i++) {
	// 		const provider = providers[i];

	// 		this.syncProvidersSubscriptions.push(
	// 			interval(providerInterval)
	// 				.pipe(startWith(0), takeUntil(this._unsubscribe))
	// 				.subscribe(() => {
	// 					this.cartService.get(this.projectId).subscribe(cartItems => {
	// 						if (this.syncProvidersData[i] && !isEqual(this.syncProvidersData[i], cartItems)) {
	// 							this.cartService.set(cartItems);
	// 							this.syncProvidersData[i] = cartItems;
	// 						}
	// 						if (!this.syncProvidersData[i]) {
	// 							this.syncProvidersData[i] = cartItems;
	// 						}
	// 					});
	// 				})
	// 		);
	// 	}
	// }

	finishExport() {
		this.isProjectFinished = true;
	}

	/* easier debugging of state changes */
	setState(state: ProjectState) {
		this.state = state;
	}

	logout() {
		this.sessionService.logout();

		this.router.navigate(['']);
		this.router.navigate(['login'], {
			replaceUrl: true,
			skipLocationChange: false
		});
	}
}
